import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createClaimLink, updateCommerceContact } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Badge, Button, C, Card, ErrorNote, Field, Muted, PhoneField, SectionTitle, Title } from '../ui';
import { isValidLocal, localDigits, toStoredPhone } from '../phone';
import { fmtDate, useCommerces } from '../useCommerces';

const Row = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: 'flex', padding: '8px 0', borderTop: `1px solid ${C.cardBorder}`, fontSize: 14 }}>
    <span style={{ width: 160, color: C.muted, flexShrink: 0 }}>{label}</span>
    <span style={{ color: C.primary }}>{value || '—'}</span>
  </div>
);

const CommerceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { commerces, loading, refresh } = useCommerces();
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);

  const c = commerces.find((x) => x.id_comercio === id);
  const claimed = c?.estado === 'reclamado';

  // Edicion de contacto (solo drafts). form.telefono guarda solo los 10 digitos nacionales.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const openEdit = () => {
    setSaveError(null);
    setPhoneError(null);
    setSavedOk(false);
    setForm({
      nombre: c?.owner_nombre || '',
      email: c?.owner_correo || '',
      telefono: localDigits(c?.owner_telefono || ''),
    });
    setEditing(true);
  };

  // Auto-abre el editor si se llego con ?edit=1 (accion rapida desde la lista).
  useEffect(() => {
    if (!loading && c && !claimed && searchParams.get('edit') === '1' && !editing) {
      openEdit();
      // React Router trata `searchParams` como inmutable: clona antes de mutar para no contaminar
      // el objeto memoizado del hook y garantizar que detecte el cambio de URL.
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [loading, c, claimed, searchParams]);

  const onClaimLink = async () => {
    if (!token || !id) return;
    setError(null);
    setGenLoading(true);
    try {
      const res = await createClaimLink(token, id);
      setLink(`${window.location.origin}/pre-registro/${res.token}`);
    } catch {
      setError('No se pudo generar el link de claim.');
    } finally {
      setGenLoading(false);
    }
  };

  const onSaveContact = async () => {
    if (!token || !id) return;
    setSaveError(null);
    setSavedOk(false);

    // El telefono es el destino del OTP del claim: si lo capturan, debe ser un MX valido de 10 digitos.
    if (form.telefono.trim() && !isValidLocal(form.telefono)) {
      setPhoneError('Ingresa un WhatsApp valido de 10 digitos.');
      return;
    }
    setPhoneError(null);

    setSaving(true);
    try {
      await updateCommerceContact(token, id, {
        nombre: form.nombre.trim() || undefined,
        email: form.email.trim() || undefined,
        telefono: form.telefono.trim() ? toStoredPhone(form.telefono) : undefined,
      });
      await refresh();
      setEditing(false);
      setSavedOk(true);
    } catch {
      setSaveError('No se pudo guardar el contacto. Revisa los datos e intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Muted>Cargando...</Muted>;
  if (!c) {
    return (
      <>
        <Title>Comercio no encontrado</Title>
        <Button variant="secondary" onClick={() => navigate('/partner/comercios')}>
          Volver a comercios
        </Button>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <Title>{c.negocio_nombre || 'Comercio sin nombre'}</Title>
        <Badge tone={claimed ? 'claimed' : 'draft'}>{claimed ? 'Reclamado' : 'Borrador'}</Badge>
      </div>

      <Card>
        <SectionTitle>Informacion</SectionTitle>
        <Row label="Negocio" value={c.negocio_nombre || ''} />
        <Row label="Dueno" value={c.owner_nombre || ''} />
        <Row label="WhatsApp" value={c.owner_telefono || ''} />
        <Row label="Email" value={c.owner_correo || ''} />
        <Row label="Plan" value={c.tipo_plan || 'FREEMIUM'} />
        <Row label="Estado" value={claimed ? `Reclamado el ${fmtDate(c.claimed_at)}` : 'Borrador (sin reclamar)'} />
        <Row label="Creado" value={fmtDate(c.created_at)} />
        {savedOk && <Muted style={{ color: C.success }}>Contacto actualizado correctamente.</Muted>}
      </Card>

      {!claimed && editing && (
        <Card>
          <SectionTitle>Editar contacto del dueno</SectionTitle>
          <Muted style={{ marginTop: 0, marginBottom: 16 }}>
            Corrige el contacto del dueno. El codigo de verificacion se envia a este WhatsApp, asi que un telefono
            incorrecto impide que el dueno reclame su cuenta.
          </Muted>
          <Field
            label="Nombre del dueno"
            value={form.nombre}
            onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
            placeholder="Nombre y apellido"
          />
          <PhoneField
            label="WhatsApp"
            value={form.telefono}
            onChange={(v) => {
              setForm((f) => ({ ...f, telefono: v }));
              if (phoneError) setPhoneError(null);
            }}
            error={phoneError}
          />
          <Field
            label="Email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="correo@ejemplo.com"
            type="email"
          />
          {saveError && <ErrorNote>{saveError}</ErrorNote>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <Button onClick={onSaveContact} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar contacto'}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {!claimed && (
        <Card>
          <SectionTitle>Acciones</SectionTitle>
          <Muted style={{ marginTop: 0 }}>
            Pre-llena la configuracion y el menu, corrige el contacto del dueno, o genera el link para que el dueno
            reclame su cuenta (valido 72h). Entre mas configures, mas rapido sera el alta del dueno.
          </Muted>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate(`/partner/draft/${c.id_comercio}/configurar`)}>Configurar comercio</Button>
            <Button variant="secondary" onClick={() => navigate(`/partner/draft/${c.id_comercio}/menu`)}>
              Cargar menu
            </Button>
            <Button variant="secondary" onClick={openEdit} disabled={editing}>
              Editar contacto
            </Button>
            <Button variant="secondary" onClick={onClaimLink} disabled={genLoading}>
              {genLoading ? 'Generando...' : 'Generar link de claim'}
            </Button>
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          {link && (
            <div style={{ marginTop: 14 }}>
              <Muted style={{ marginTop: 0, marginBottom: 6 }}>Comparte este link con el dueno:</Muted>
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 13,
                  border: `1px solid ${C.inputBorder}`,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  fontFamily: 'Poppins, sans-serif',
                  color: C.secondaryDark,
                }}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
};

export default CommerceDetail;
